import { useState, useCallback } from 'react';
import axios from 'axios';

const defaultChannelFormData = {
  name: '',
  defaultLocation: '',
  channelLink: '',
  useItemNumber: false,
  useCustomItemNumber: false,
  itemNumberPrefix: '',
  itemNumberStart: '',
  labelRequired: false,
  barcodeRequired: false,
  barcodeFormat: '',
  barcodeSource: 'itemNumber',
  quailhqEnabled: false,
  quailhqAccountId: '',
  quailBoothNumber: '',
  chargesCommission: false,
  commissionPercentage: '',
};

const settingsArrayToFlat = (settings) => {
  const flat = {
    useItemNumber: false,
    useCustomItemNumber: false,
    itemNumberPrefix: '',
    itemNumberStart: '',
    labelRequired: false,
    barcodeRequired: false,
    barcodeFormat: '',
    barcodeSource: 'itemNumber',
    quailhqEnabled: false,
    quailhqAccountId: '',
    quailBoothNumber: '',
    chargesCommission: false,
    commissionPercentage: '',
  };
  if (!Array.isArray(settings)) return flat;
  for (const s of settings) {
    if (s.key === 'useItemNumber') flat.useItemNumber = s.value === true;
    else if (s.key === 'useCustomItemNumber') flat.useCustomItemNumber = s.value === true;
    else if (s.key === 'itemNumberPrefix') flat.itemNumberPrefix = s.value || '';
    else if (s.key === 'itemNumberStart') flat.itemNumberStart = s.value || '';
    else if (s.key === 'labelRequired') flat.labelRequired = s.value === true;
    else if (s.key === 'barcodeRequired') flat.barcodeRequired = s.value === true;
    else if (s.key === 'barcodeFormat') flat.barcodeFormat = s.value || '';
    else if (s.key === 'barcodeSource') flat.barcodeSource = s.value || 'itemNumber';
    else if (s.key === 'quailhqEnabled') flat.quailhqEnabled = s.value === true;
    else if (s.key === 'quailhqAccountId') flat.quailhqAccountId = s.value || '';
    else if (s.key === 'quailBoothNumber') flat.quailBoothNumber = s.value || '';
    else if (s.key === 'chargesCommission') flat.chargesCommission = s.value === true;
    else if (s.key === 'commissionPercentage') flat.commissionPercentage = s.value ?? '';
  }
  return flat;
};

const flatToSettingsArray = (formData) => {
  const settings = [];

  // useItemNumber settings (independent of label/barcode)
  settings.push({ key: 'useItemNumber', value: !!formData.useItemNumber });
  if (formData.useItemNumber) {
    settings.push({ key: 'useCustomItemNumber', value: !!formData.useCustomItemNumber });
    if (!formData.useCustomItemNumber) {
      settings.push({ key: 'itemNumberPrefix', value: formData.itemNumberPrefix });
      settings.push({ key: 'itemNumberStart', value: formData.itemNumberStart });
    }
  }

  // QuailHQ settings
  if (formData.quailhqEnabled) {
    settings.push({ key: 'quailhqEnabled', value: true });
    settings.push({ key: 'quailhqAccountId', value: formData.quailhqAccountId });
    settings.push({ key: 'quailBoothNumber', value: formData.quailBoothNumber });
  }

  // Label/barcode settings
  settings.push({ key: 'labelRequired', value: !!formData.labelRequired });
  if (formData.labelRequired) {
    settings.push({ key: 'barcodeRequired', value: !!formData.barcodeRequired });
    settings.push({ key: 'barcodeSource', value: formData.barcodeSource || 'itemNumber' });
    if (formData.barcodeRequired) {
      settings.push({ key: 'barcodeFormat', value: formData.barcodeFormat });
    }
  }

  // Commission settings
  settings.push({ key: 'chargesCommission', value: !!formData.chargesCommission });
  if (formData.chargesCommission) {
    settings.push({ key: 'commissionPercentage', value: Number(formData.commissionPercentage) });
  }

  return settings;
};

const useChannelsData = ({
  salesChannelsUrl,
  getAuthHeaders,
  pendingCheckout,
  showSuccessMessage,
  setError,
  needsInitialChannel,
  setNeedsInitialChannel,
  accountId,
}) => {
  const [channels, setChannels] = useState(null);
  const [isLoadingChannels, setIsLoadingChannels] = useState(false);
  const [showChannelForm, setShowChannelForm] = useState(false);
  const [channelFormData, setChannelFormData] = useState({ ...defaultChannelFormData });
  const [editingChannel, setEditingChannel] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchChannels = useCallback(async () => {
    if (pendingCheckout) return;

    setIsLoadingChannels(true);
    try {
      const config = await getAuthHeaders();
      const response = await axios.get(salesChannelsUrl, config);
      setChannels(response.data.data.items || []);
    } catch (err) {
      console.error('Error fetching channels:', err);
    } finally {
      setIsLoadingChannels(false);
    }
  }, [salesChannelsUrl, getAuthHeaders, pendingCheckout]);

  const handleChannelInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'useItemNumber') {
      setChannelFormData(prev => ({
        ...prev,
        useItemNumber: value,
        ...(value ? {} : { useCustomItemNumber: false, itemNumberPrefix: '', itemNumberStart: '' }),
      }));
    } else if (name === 'useCustomItemNumber') {
      setChannelFormData(prev => ({
        ...prev,
        useCustomItemNumber: value,
        ...(value ? { itemNumberPrefix: '', itemNumberStart: '' } : {}),
      }));
    } else if (name === 'quailhqEnabled') {
      setChannelFormData(prev => ({
        ...prev,
        quailhqEnabled: value,
        ...(value
          ? { barcodeSource: 'externalBarcodeNumber' }
          : { quailhqAccountId: '', quailBoothNumber: '', barcodeSource: 'itemNumber' }),
      }));
    } else if (name === 'labelRequired') {
      setChannelFormData(prev => ({
        ...prev,
        labelRequired: value,
        ...(value ? {} : { barcodeRequired: false, barcodeFormat: '' }),
      }));
    } else if (name === 'barcodeRequired') {
      setChannelFormData(prev => ({
        ...prev,
        barcodeRequired: value,
        ...(value ? {} : { barcodeFormat: '' }),
      }));
    } else {
      setChannelFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleChannelSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Validate QuailHQ settings when enabled
    if (channelFormData.quailhqEnabled && !channelFormData.quailhqAccountId?.trim()) {
      setError('QuailHQ account ID is required when QuailHQ integration is enabled.');
      setIsSubmitting(false);
      return;
    }
    if (channelFormData.quailhqEnabled && !channelFormData.quailBoothNumber?.trim()) {
      setError('Booth number is required when QuailHQ integration is enabled.');
      setIsSubmitting(false);
      return;
    }

    // Validate item number settings when enabled (skip if using custom item numbers)
    if (channelFormData.useItemNumber && !channelFormData.useCustomItemNumber) {
      if (!channelFormData.itemNumberStart.trim() || !/^\d+$/.test(channelFormData.itemNumberStart)) {
        setError('Item number start must be a non-empty number (digits only).');
        setIsSubmitting(false);
        return;
      }
    }

    // Validate barcode settings when enabled
    if (channelFormData.labelRequired && channelFormData.barcodeRequired) {
      const validFormats = ['UPC', 'Code 128', 'Code 39', 'QR Code'];
      if (!validFormats.includes(channelFormData.barcodeFormat)) {
        setError('Please select a valid barcode format.');
        setIsSubmitting(false);
        return;
      }
    }

    // Validate commission settings when enabled
    if (channelFormData.chargesCommission) {
      const raw = channelFormData.commissionPercentage;
      if (raw === '' || raw === null || raw === undefined) {
        setError('Commission percentage is required when commission is enabled.');
        setIsSubmitting(false);
        return;
      }
      const pct = Number(raw);
      if (!Number.isFinite(pct)) {
        setError('Commission percentage must be a number.');
        setIsSubmitting(false);
        return;
      }
      if (pct < 1 || pct > 100) {
        setError('Commission percentage must be between 1 and 100.');
        setIsSubmitting(false);
        return;
      }
      if (Math.round(pct * 100) !== pct * 100) {
        setError('Commission percentage must have at most 2 decimal places.');
        setIsSubmitting(false);
        return;
      }
    }

    try {
      const config = await getAuthHeaders();
      const settings = flatToSettingsArray(channelFormData);
      const payload = {
        name: channelFormData.name,
        defaultLocation: channelFormData.defaultLocation,
        channelLink: channelFormData.channelLink,
        settings,
        accountId,
      };
      if (editingChannel) {
        const response = await axios.put(`${salesChannelsUrl}/${editingChannel.id}`, payload, config);
        if (response.data.success) {
          const updatedChannel = response.data.data;
          setChannels(prevChannels => prevChannels.map(ch => ch.id === editingChannel.id ? updatedChannel : ch));
          showSuccessMessage('Channel updated successfully!');
        }
      } else {
        const response = await axios.post(salesChannelsUrl, payload, config);
        if (response.data.success) {
          fetchChannels();
          showSuccessMessage('Channel created successfully!');

          if (needsInitialChannel) {
            setNeedsInitialChannel(false);
          }
        }
      }
      setChannelFormData({ ...defaultChannelFormData });
      setEditingChannel(null);
      setShowChannelForm(false);
    } catch (error) {
      console.error('Error saving channel:', error);
      if (error.response?.data?.validationErrors) {
        setError(error.response.data.validationErrors.join(', '));
      } else {
        setError(error.response?.data?.error || 'Failed to save channel');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEditingChannel = (channel) => {
    setEditingChannel(channel);
    const flatSettings = settingsArrayToFlat(channel.settings);
    setChannelFormData({
      name: channel.name,
      defaultLocation: channel.defaultLocation || '',
      channelLink: channel.channelLink || '',
      ...flatSettings,
    });
    setShowChannelForm(true);
  };

  const cancelChannelEdit = () => {
    if (needsInitialChannel) {
      return;
    }
    setEditingChannel(null);
    setChannelFormData({ ...defaultChannelFormData });
    setShowChannelForm(false);
  };

  const deleteChannel = async (channelId) => {
    try {
      const config = await getAuthHeaders();
      const response = await axios.delete(`${salesChannelsUrl}/${channelId}`, config);
      if (response.data.success) {
        setChannels(prevChannels => prevChannels.filter(ch => ch.id !== channelId));
        showSuccessMessage('Channel deleted successfully!');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error deleting channel:', error);
      setError(error.response?.data?.error || 'Failed to delete channel');
      return false;
    }
  };

  const fetchNextItemNumber = async (channelId) => {
    try {
      const config = await getAuthHeaders();
      const response = await axios.get(`${salesChannelsUrl}/${channelId}/next-item-number`, config);
      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch {
      return null;
    }
  };

  return {
    channels,
    setChannels,
    isSubmitting,
    isLoadingChannels,
    showChannelForm,
    setShowChannelForm,
    channelFormData,
    setChannelFormData,
    editingChannel,
    setEditingChannel,
    fetchChannels,
    handleChannelInputChange,
    handleChannelSubmit,
    startEditingChannel,
    cancelChannelEdit,
    deleteChannel,
    fetchNextItemNumber,
  };
};

export default useChannelsData;
